import React, { useEffect, useMemo, useState } from 'react';
import ModalShell from '../ModalShell';
import {
  IconDelete,
  IconEdit,
  IconSave,
  IconCancel,
  iconBtnStyle,
} from '../../shared/icons/IconLib';
import {
  normalizeEntityLabelName,
  validateEntityLabelName,
} from '../../shared/utils/entityLabelFilter';

/**
 * Labels modal: one row per label — select, rename, delete; add new at bottom.
 */
export default function EntityLabelsModal({
  open,
  onClose,
  labelOptions = [],
  selectedLabels = [],
  onApplyLabels,
  onAddLabel,
  onRenameLabel,
  onRemoveLabel,
  setConfirmDialog,
  saving = false,
  title,
  subtitle,
}) {
  const [selection, setSelection] = useState(new Set());
  const [pendingOptions, setPendingOptions] = useState([]);
  const [newLabelName, setNewLabelName] = useState('');
  const [addError, setAddError] = useState('');
  const [editingLabel, setEditingLabel] = useState(null);
  const [editBuffer, setEditBuffer] = useState('');
  const [editError, setEditError] = useState('');

  const modalTitle = title || 'Labels';
  const modalSubtitle =
    subtitle || 'Select labels for this item. Rename or delete from the same row.';

  useEffect(() => {
    if (!open) return;
    setSelection(new Set((selectedLabels || []).map((name) => normalizeEntityLabelName(name)).filter(Boolean)));
    setPendingOptions([]);
    setNewLabelName('');
    setAddError('');
    setEditingLabel(null);
    setEditBuffer('');
    setEditError('');
  }, [open, selectedLabels]);

  const sortedOptions = useMemo(() => {
    const names = new Set([...(labelOptions || []), ...pendingOptions]);
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [labelOptions, pendingOptions]);

  function toggleSelection(name) {
    setSelection((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }

  async function applySelection(nextSelection) {
    const sorted = [...nextSelection].sort((a, b) => a.localeCompare(b));
    if (!onApplyLabels) {
      onClose();
      return;
    }
    try {
      await onApplyLabels(sorted);
      onClose();
    } catch {
      // caller surfaces error
    }
  }

  async function handleAddLabel(e) {
    e?.preventDefault();
    const validated = validateEntityLabelName(newLabelName);
    if (!validated.ok) {
      setAddError(validated.error);
      return;
    }
    if (sortedOptions.includes(validated.label)) {
      setAddError('That label already exists.');
      return;
    }
    const result = onAddLabel(validated.label);
    if (result && result.ok === false) {
      setAddError(result.error || 'Failed to add label');
      return;
    }
    const nextSelection = new Set(selection);
    nextSelection.add(validated.label);
    setPendingOptions((prev) =>
      prev.includes(validated.label) ? prev : [...prev, validated.label].sort((a, b) => a.localeCompare(b))
    );
    setSelection(nextSelection);
    setNewLabelName('');
    setAddError('');
    await applySelection(nextSelection);
  }

  function startEdit(name) {
    setEditingLabel(name);
    setEditBuffer(name);
    setEditError('');
  }

  function cancelEdit() {
    setEditingLabel(null);
    setEditBuffer('');
    setEditError('');
  }

  async function commitEdit() {
    if (!editingLabel) return;
    const validated = validateEntityLabelName(editBuffer);
    if (!validated.ok) {
      setEditError(validated.error);
      return;
    }
    if (validated.label !== editingLabel && sortedOptions.includes(validated.label)) {
      setEditError('That label already exists.');
      return;
    }
    if (validated.label === editingLabel) {
      cancelEdit();
      return;
    }
    try {
      await onRenameLabel(editingLabel, validated.label);
      setSelection((prev) => {
        const next = new Set(prev);
        if (next.has(editingLabel)) {
          next.delete(editingLabel);
          next.add(validated.label);
        }
        return next;
      });
      cancelEdit();
    } catch (err) {
      setEditError(err.message || 'Failed to rename label');
    }
  }

  function handleDeleteLabel(name) {
    setConfirmDialog({
      open: true,
      title: 'Delete label',
      mode: 'confirm',
      message: `Delete label "${name}" and remove it from all items in this list?`,
      onConfirm: async () => {
        try {
          await onRemoveLabel(name);
          setSelection((prev) => {
            const next = new Set(prev);
            next.delete(name);
            return next;
          });
          if (editingLabel === name) cancelEdit();
        } catch (err) {
          setConfirmDialog({
            open: true,
            title: 'Delete label',
            message: err.message || 'Failed to delete label',
            mode: 'alert',
            onConfirm: null,
          });
        }
      },
    });
  }

  async function handleApply() {
    await applySelection(selection);
  }

  const busy = !!saving;

  return (
    <ModalShell
      open={open}
      onClose={busy ? undefined : onClose}
      title={modalTitle}
      subtitle={modalSubtitle}
      maxWidth={560}
      footer={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button type="button" className="btn btn-md btn-secondary" disabled={busy} onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-md btn-primary" disabled={busy} onClick={handleApply}>
            {busy ? 'Saving…' : 'Apply'}
          </button>
        </div>
      }
    >
      {sortedOptions.length === 0 ? (
        <p className="panel-hint" style={{ marginTop: 0, marginBottom: 12 }}>
          No labels yet. Add one below with Add &amp; Apply to create and assign it in one step.
        </p>
      ) : (
        <ul className="entity-labels-modal__list">
          {sortedOptions.map((name) => {
            const isEditing = editingLabel === name;
            return (
              <li key={name} className="entity-labels-modal__row">
                {isEditing ? (
                  <>
                    <span className="entity-labels-modal__checkbox-spacer" aria-hidden="true" />
                    <input
                      type="text"
                      className="form-control entity-labels-modal__edit-input"
                      value={editBuffer}
                      disabled={busy}
                      maxLength={80}
                      onChange={(e) => {
                        setEditBuffer(e.target.value);
                        if (editError) setEditError('');
                      }}
                    />
                    <span className="entity-labels-modal__row-actions">
                      <button
                        type="button"
                        className="btn-link"
                        style={iconBtnStyle}
                        title="Save"
                        disabled={busy}
                        onClick={() => commitEdit()}
                      >
                        <IconSave size={18} />
                      </button>
                      <button
                        type="button"
                        className="btn-link"
                        style={iconBtnStyle}
                        title="Cancel"
                        disabled={busy}
                        onClick={cancelEdit}
                      >
                        <IconCancel size={18} />
                      </button>
                    </span>
                  </>
                ) : (
                  <>
                    <input
                      type="checkbox"
                      className="entity-labels-modal__checkbox"
                      checked={selection.has(name)}
                      disabled={busy}
                      aria-label={`Select ${name}`}
                      onChange={() => toggleSelection(name)}
                    />
                    <span className="entity-labels-modal__name">{name}</span>
                    <span className="entity-labels-modal__row-actions">
                      <button
                        type="button"
                        className="btn-link"
                        style={iconBtnStyle}
                        title="Rename"
                        disabled={busy}
                        onClick={() => startEdit(name)}
                      >
                        <IconEdit size={18} />
                      </button>
                      <button
                        type="button"
                        className="btn-link-danger"
                        style={iconBtnStyle}
                        title="Delete"
                        disabled={busy}
                        onClick={() => handleDeleteLabel(name)}
                      >
                        <IconDelete size={18} />
                      </button>
                    </span>
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {editError ? (
        <p style={{ marginTop: 8, marginBottom: 0, color: '#dc3545', fontSize: 13 }}>{editError}</p>
      ) : null}

      <form onSubmit={handleAddLabel} className="entity-labels-modal__add-form">
        <input
          type="text"
          className="form-control"
          placeholder="New label name…"
          value={newLabelName}
          disabled={busy}
          maxLength={80}
          onChange={(e) => {
            setNewLabelName(e.target.value);
            if (addError) setAddError('');
          }}
        />
        <button type="submit" className="btn btn-md btn-primary-outline" disabled={busy}>
          {busy ? 'Saving…' : 'Add & Apply'}
        </button>
      </form>
      {addError ? (
        <p style={{ marginTop: 8, marginBottom: 0, color: '#dc3545', fontSize: 13 }}>{addError}</p>
      ) : null}
    </ModalShell>
  );
}
